import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { MicButton } from '@/components/ui/MicButton';
import { meetingsApi, transcribeBlob, type Participant, type MeetingDetail } from '@/lib/meetings';
import { useMicRecorder } from '@/hooks/useMicRecorder';

export function MeetingSetup() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const nav = useNavigate();

  const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
  const [title, setTitle] = useState('');
  const [briefMd, setBriefMd] = useState('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [language, setLanguage] = useState('auto');
  const [participantInput, setParticipantInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recorder = useMicRecorder();
  const [transcribing, setTranscribing] = useState(false);

  useEffect(() => {
    if (isNew) return;
    meetingsApi
      .get(id!)
      .then((m) => {
        setMeeting(m);
        setTitle(m.title);
        setBriefMd(m.brief_md ?? '');
        setParticipants(m.participants ?? []);
        setLanguage(m.primary_language || 'auto');
      })
      .catch(() => setError('Не удалось загрузить встречу'));
  }, [id, isNew]);

  const dirty = useMemo(() => {
    if (!meeting) return isNew && (title.trim() || briefMd.trim() || participants.length > 0);
    return (
      title !== meeting.title ||
      briefMd !== (meeting.brief_md ?? '') ||
      JSON.stringify(participants) !== JSON.stringify(meeting.participants ?? []) ||
      language !== meeting.primary_language
    );
  }, [meeting, title, briefMd, participants, language, isNew]);

  const save = async (next?: 'live') => {
    if (!title.trim()) {
      setError('Назовите встречу');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let mid = id;
      if (isNew) {
        const r = await meetingsApi.create({
          title: title.trim(),
          brief_md: briefMd,
          participants,
          primary_language: language,
        });
        mid = r.id;
      } else {
        await meetingsApi.update(id!, {
          title: title.trim(),
          brief_md: briefMd,
          participants,
          primary_language: language,
        });
      }
      if (next === 'live') {
        nav(`/m/${mid}/live`);
      } else {
        nav('/');
      }
    } catch (e: any) {
      setError(e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!id || isNew) return;
    if (!confirm('Удалить встречу безвозвратно?')) return;
    await meetingsApi.remove(id);
    nav('/');
  };

  const addParticipant = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    const m = t.match(/^(.+?)\s*\((.+)\)\s*$/);
    if (m && m[1] && m[2]) {
      setParticipants((p) => [...p, { name: m[1]!.trim(), role: m[2]!.trim() }]);
    } else {
      setParticipants((p) => [...p, { name: t }]);
    }
    setParticipantInput('');
  };

  const onMicStart = async () => {
    try {
      await recorder.start();
    } catch (e: any) {
      setError(e?.message || 'Не удалось получить доступ к микрофону');
    }
  };

  const onMicStop = async () => {
    setTranscribing(true);
    try {
      const blob = await recorder.stop();
      if (blob.size === 0) return;
      const langHint = language === 'auto' ? 'ru' : language;
      const text = await transcribeBlob(blob, langHint);
      setBriefMd((prev) => (prev ? `${prev}\n${text}` : text));
    } catch (e: any) {
      setError(e?.message || 'Не удалось расшифровать');
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col px-5 pt-5 pb-24">
      <header className="flex items-center justify-between mb-4">
        <Link to="/" className="btn-ghost gap-1 -ml-2">
          <BackIcon /> Назад
        </Link>
        <h1 className="text-[15px] font-semibold">
          {isNew ? 'Новая встреча' : 'Брифинг'}
        </h1>
        <span className="size-8" />
      </header>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4 flex-1"
      >
        <Card>
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">
              Название
            </div>
            <input
              className="input-base"
              placeholder="Например: Звонок с инвестором"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
            />
          </label>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-ink-3">
              Контекст для AI
            </div>
            <MicButton
              state={
                transcribing
                  ? 'busy'
                  : recorder.state === 'recording'
                    ? 'recording'
                    : recorder.state === 'stopping'
                      ? 'stopping'
                      : 'idle'
              }
              onStart={onMicStart}
              onStop={onMicStop}
            />
          </div>
          <textarea
            className="input-base min-h-[160px] font-mono text-[14px] leading-relaxed"
            placeholder="Куда идёшь? О чём встреча? Что важно? Что не должно всплыть?&#10;&#10;Можно надиктовать через микрофон справа."
            value={briefMd}
            onChange={(e) => setBriefMd(e.target.value)}
            maxLength={60000}
          />
          <p className="text-[11px] text-ink-3 mt-2">
            {transcribing
              ? 'Расшифровываю…'
              : recorder.state === 'recording'
                ? 'Запись… нажмите снова, чтобы остановить.'
                : `${briefMd.length} символов · чем больше деталей, тем точнее подсказки`}
          </p>
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">
            Участники
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {participants.map((p, i) => (
              <span key={i} className="chip text-[13px]">
                {p.name}
                {p.role ? <span className="text-ink-3">· {p.role}</span> : null}
                <button
                  className="text-ink-3 hover:text-danger ml-1"
                  onClick={() => setParticipants((arr) => arr.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
            {participants.length === 0 && (
              <span className="text-xs text-ink-3">пока пусто</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              className="input-base flex-1"
              placeholder="Имя или Имя (роль)"
              value={participantInput}
              onChange={(e) => setParticipantInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addParticipant(participantInput);
                }
              }}
            />
            <Button
              variant="outline"
              size="md"
              onClick={() => addParticipant(participantInput)}
              disabled={!participantInput.trim()}
            >
              +
            </Button>
          </div>
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">
            Основной язык
          </div>
          <div className="flex flex-wrap gap-2">
            {(['auto', 'ru', 'en', 'kk', 'ky', 'uz', 'tr', 'es', 'de', 'fr'] as const).map(
              (l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLanguage(l)}
                  className={clsx(
                    'rounded-pill px-3.5 py-1.5 text-sm transition focus-ring',
                    language === l
                      ? 'bg-accent text-white shadow-glow'
                      : 'glass-strong text-ink-1 hover:bg-white/[0.1]',
                  )}
                >
                  {l === 'auto' ? 'Авто' : l.toUpperCase()}
                </button>
              ),
            )}
          </div>
          <p className="text-[11px] text-ink-3 mt-3">
            AI всё равно понимает несколько языков одновременно. Этот выбор —
            подсказка для расстановки приоритетов.
          </p>
        </Card>

        {error && <div className="text-sm text-danger px-2">{error}</div>}
      </motion.div>

      <footer className="fixed bottom-0 inset-x-0 px-5 pb-5 pt-3 bg-gradient-to-t from-bg-0 via-bg-0/95 to-transparent">
        <div className="max-w-screen-md mx-auto flex gap-2">
          {!isNew && (
            <Button variant="outline" onClick={remove}>
              <TrashIcon />
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => save()}
            disabled={saving || !title.trim()}
          >
            Сохранить
          </Button>
          <Button
            full
            onClick={() => save('live')}
            disabled={saving || !title.trim()}
          >
            {dirty ? 'Сохранить и начать' : 'Начать встречу'} →
          </Button>
        </div>
      </footer>
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M12 4l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 7h18M9 7V4h6v3M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13M10 11v7M14 11v7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
